/**
 * IndexedDB persistence for `blockCollection`.
 *
 * On boot the homepage paints cards from whatever envelopes
 * `mainStreamStore` has loaded — a fresh tab with no cache has to wait
 * for `/v1/block/list` to round-trip before the first card appears.
 * That request currently takes 300-1000ms (Cloudflare → Hyperdrive →
 * Postgres). We can cut the visible wait to ~20ms on every reload
 * after the first by caching the last known envelope snapshot in
 * IndexedDB and replaying it into `blockCollection` before the
 * network even fires.
 *
 * Why `idb-keyval` (and not raw `indexedDB` or `localforage`):
 *   - 1 KB gzipped. Doesn't add to the bundle in any meaningful way.
 *   - Single-key get/set is exactly the shape we need — the whole
 *     snapshot is one JSON blob, not a normalized table.
 *   - The cache is best-effort: on read failure / schema mismatch we
 *     fall back to an empty seed and the live network response fills
 *     the gap. No migration story needed beyond bumping the schema
 *     version key when the envelope shape changes incompatibly.
 *
 * Trade-offs captured here so future readers don't relitigate them:
 *
 *   - Reads are async (no top-level await). `block-collection.ts`'s
 *     `hydrateFromIdb` kicks the read at module load and writes the
 *     snapshot into the collection via its manual-sync utils once the
 *     IDB read resolves. First paint typically lands before this
 *     resolves on a cold load, but it materially helps warm visits.
 *
 *   - Writes are debounced (default 250ms) so a `writeBatch` of 100
 *     envelopes doesn't flush 100 times. The snapshot is the entire
 *     current cache, so writes are O(N) — keep N bounded via
 *     `MAX_PERSISTED`.
 *
 *   - We do NOT persist incremental deltas. Replaying deltas is more
 *     complex than re-serializing the whole map, and the cache is
 *     small (≤ 200 envelopes × ~2 KB = ~400 KB). If telemetry shows
 *     this is too big for slow phones, switch to a delta log.
 *
 *   - Schema version is checked on read. A bump means everyone loses
 *     their cache once; the next homepage `listBlocks` reseeds.
 *     Acceptable — the cache is purely an optimization.
 */
import { del, get, set } from "idb-keyval";
/**
 * Bumped to invalidate every existing user's persisted envelope
 * snapshot. v1 cached envelopes from older bundles were suspected
 * of interfering with the optimistic-placeholder write path on
 * long-lived clients (PWA, normal Brave) — incognito works fine,
 * which points at persistent state. Old `v1` keys become orphans
 * in IDB but are never read again; no migration needed because the
 * cache is purely a fast-path optimization.
 */
const SCHEMA_VERSION = 2;
const STORAGE_KEY = `viewer-block-envelopes:v${SCHEMA_VERSION}`;
/**
 * Cap on how many envelopes we persist. The homepage's initial seed
 * loads 100; we keep a little headroom so a user who scrolled into
 * page 2 before navigating away gets their next visit's first paint
 * from cache too. Above this number the persisted set is truncated
 * by `surfaceAt desc` order (newest first).
 */
const MAX_PERSISTED = 200;
/**
 * Schema version + key for the homepage stream's persisted block-id
 * list. Tracked separately from the envelope snapshot because the
 * two have independent lifecycles: envelopes are global (the cache
 * is shared across surfaces), but `blockIds` is the homepage feed's
 * own pointer set — the thing the strict-subset filter joins
 * against. Without persisting it, IDB-hydrated envelopes sit in the
 * shared cache but can't render on the homepage because the live
 * query's `inArray(m.block.id, blockIds)` filter is empty.
 *
 * Bumping this version invalidates every user's persisted id list;
 * the next homepage mount re-seeds via `listBlocks`. Acceptable —
 * the snapshot is purely a fast-path optimization, not authoritative.
 */
const STREAM_BLOCK_IDS_SCHEMA_VERSION = 1;
const STREAM_BLOCK_IDS_STORAGE_KEY = `viewer-stream-block-ids:v${STREAM_BLOCK_IDS_SCHEMA_VERSION}`;
/**
 * Cap on how many ids we persist. Matched to `MAX_PERSISTED` above
 * so the id list never references envelopes that the envelope
 * snapshot has truncated away (orphan pointers render as pending
 * skeletons until the seed fetch resolves them). Truncation order
 * matches what the caller writes — typically `surfaceAt desc` from
 * `mainStreamStore`, i.e. newest first, oldest dropped.
 */
const MAX_STREAM_BLOCK_IDS_PERSISTED = 200;
/**
 * Schema version + key for the cross-layer collection cache snapshot.
 * Tracked independently from the block envelope / stream-block-id
 * snapshots because the three caches have unrelated shapes and
 * evolve on their own cadences.
 *
 * Why persist collections at all: the homepage Stream's
 * same-collection-per-day synthetic-stack grouping
 * (`mergeSameCollectionBlocks`) reads from `collectionCollection`
 * to resolve each block's effective parent. Without IDB persistence
 * the cache is empty on warm load until `/v1/bootstrap` returns —
 * blocks paint from their own IDB cache (~20ms) but the grouping
 * step has no lookup data, so every block lands as a bare card.
 * When bootstrap finally lands the grouping re-runs and blocks
 * collapse into Stack tiles, producing a visible "unfurled then
 * gathered" flash.
 *
 * Caching collections to IDB lets both halves of the synthetic-stack
 * derivation (envelopes + lookup map) paint in the same React tick
 * on warm visits — no flash.
 *
 * Bumping `COLLECTIONS_SCHEMA_VERSION` invalidates every user's
 * persisted snapshot; the next bootstrap reseeds. Acceptable — the
 * cache is purely a fast-path optimization.
 */
const COLLECTIONS_SCHEMA_VERSION = 1;
const COLLECTIONS_STORAGE_KEY = `viewer-collections:v${COLLECTIONS_SCHEMA_VERSION}`;
/**
 * Cap on how many collections we persist. Collections are small (~200
 * bytes each, no nested artifact graphs) so the cap is intentionally
 * generous compared to `MAX_PERSISTED` for blocks — a power user with
 * hundreds of Groups + Stacks + private Collections still fits well
 * under a megabyte. Above this we truncate by `lastAddedAt desc` so
 * the most recently active rows are the ones that survive.
 */
const MAX_COLLECTIONS_PERSISTED = 1000;
/**
 * Read the persisted envelope snapshot. Resolves to `[]` on any
 * failure path — missing key, schema mismatch, parse error, IDB
 * unavailable (private-mode Safari).
 */
export async function readBlockEnvelopesSnapshot() {
    try {
        const raw = await get(STORAGE_KEY);
        if (!raw || raw.schema !== SCHEMA_VERSION)
            return [];
        if (!Array.isArray(raw.envelopes))
            return [];
        return raw.envelopes;
    }
    catch (e) {
        console.warn("[idb-persistence] read failed:", e);
        return [];
    }
}
/**
 * Drop the persisted envelope snapshot. Called on logout so the next
 * user (or the next login of the same user after deliberate sign-out)
 * doesn't briefly see the previous session's cards before bootstrap
 * lands. Best-effort: a write failure is logged and swallowed.
 */
export async function clearBlockEnvelopesSnapshot() {
    try {
        await del(STORAGE_KEY);
    }
    catch (e) {
        console.warn("[idb-persistence] clear failed:", e);
    }
}
/**
 * Persist an envelope snapshot. Truncates to `MAX_PERSISTED`, sorted
 * by `surfaceAt desc` so the newest stuff is the most likely to be
 * useful on next paint.
 */
export async function writeBlockEnvelopesSnapshot(envelopes) {
    try {
        const sorted = [...envelopes].sort((a, b) => b.block.surfaceAt.localeCompare(a.block.surfaceAt));
        const truncated = sorted.slice(0, MAX_PERSISTED);
        const payload = {
            schema: SCHEMA_VERSION,
            envelopes: truncated,
            savedAt: new Date().toISOString(),
        };
        await set(STORAGE_KEY, payload);
    }
    catch (e) {
        console.warn("[idb-persistence] write failed:", e);
    }
}
/**
 * Read the persisted homepage-stream block-id list. Same failure
 * semantics as `readBlockEnvelopesSnapshot`: returns `[]` on any
 * problem (missing key, schema mismatch, parse error, IDB
 * unavailable). The caller treats an empty result as "nothing to
 * hydrate" and falls through to the cold-load network seed.
 */
export async function readStreamBlockIdsSnapshot() {
    try {
        const raw = await get(STREAM_BLOCK_IDS_STORAGE_KEY);
        if (!raw || raw.schema !== STREAM_BLOCK_IDS_SCHEMA_VERSION)
            return [];
        if (!Array.isArray(raw.blockIds))
            return [];
        return raw.blockIds;
    }
    catch (e) {
        console.warn("[idb-persistence] stream block-ids read failed:", e);
        return [];
    }
}
/**
 * Persist the homepage-stream block-id list. Truncates to
 * `MAX_STREAM_BLOCK_IDS_PERSISTED` from the head — callers pass the
 * already-display-sorted ids (typically `surfaceAt desc`), so the
 * head is the newest and therefore the most useful for next paint.
 *
 * Empty input is intentionally a no-op rather than a write. A
 * `[]` write would clobber the previous good snapshot during the
 * brief windows when `mainStreamStore.blockIds` is empty: between a
 * `resetPagination()` wipe and the next seed landing, or between a
 * type-filter switch and the filtered view's first page resolving.
 * Clearing the snapshot has its own dedicated path
 * (`clearStreamBlockIdsSnapshot`) for the sign-out case.
 */
export async function writeStreamBlockIdsSnapshot(blockIds) {
    if (blockIds.length === 0)
        return;
    try {
        const truncated = blockIds.slice(0, MAX_STREAM_BLOCK_IDS_PERSISTED);
        const payload = {
            schema: STREAM_BLOCK_IDS_SCHEMA_VERSION,
            blockIds: [...truncated],
            savedAt: new Date().toISOString(),
        };
        await set(STREAM_BLOCK_IDS_STORAGE_KEY, payload);
    }
    catch (e) {
        console.warn("[idb-persistence] stream block-ids write failed:", e);
    }
}
/**
 * Drop the persisted homepage-stream block-id snapshot. Called from
 * the sign-out path alongside `clearBlockEnvelopesSnapshot` so the
 * next login (potentially a different user) doesn't briefly see the
 * previous user's homepage feed. Best-effort: failures are logged.
 */
export async function clearStreamBlockIdsSnapshot() {
    try {
        await del(STREAM_BLOCK_IDS_STORAGE_KEY);
    }
    catch (e) {
        console.warn("[idb-persistence] stream block-ids clear failed:", e);
    }
}
/**
 * Read the persisted cross-layer collection snapshot. Same failure
 * semantics as the block envelope reader — any problem (missing key,
 * schema mismatch, parse error, IDB unavailable in private-mode
 * Safari) resolves to `[]` so callers fall through to the cold-load
 * bootstrap seed.
 */
export async function readCollectionsSnapshot() {
    try {
        const raw = await get(COLLECTIONS_STORAGE_KEY);
        if (!raw || raw.schema !== COLLECTIONS_SCHEMA_VERSION)
            return [];
        if (!Array.isArray(raw.collections))
            return [];
        return raw.collections;
    }
    catch (e) {
        console.warn("[idb-persistence] collections read failed:", e);
        return [];
    }
}
/**
 * Persist the cross-layer collection cache. Truncates to
 * `MAX_COLLECTIONS_PERSISTED`, sorted by `lastAddedAt` (falling back
 * to `createdAt`) so the most recently active rows survive truncation
 * — those are the ones the homepage synthetic-stack grouping is most
 * likely to need on next paint.
 *
 * Empty input is intentionally a no-op (mirrors
 * `writeStreamBlockIdsSnapshot`): an `[]` write would clobber the
 * previous good snapshot during brief windows when the live cache is
 * empty (logout transition, schema reset, etc.). The dedicated clear
 * path (`clearCollectionsSnapshot`) handles those cases explicitly.
 */
export async function writeCollectionsSnapshot(collections) {
    if (collections.length === 0)
        return;
    try {
        const sorted = [...collections].sort((a, b) => {
            const aKey = a.lastAddedAt ?? a.createdAt;
            const bKey = b.lastAddedAt ?? b.createdAt;
            return bKey.localeCompare(aKey);
        });
        const truncated = sorted.slice(0, MAX_COLLECTIONS_PERSISTED);
        const payload = {
            schema: COLLECTIONS_SCHEMA_VERSION,
            collections: truncated,
            savedAt: new Date().toISOString(),
        };
        await set(COLLECTIONS_STORAGE_KEY, payload);
    }
    catch (e) {
        console.warn("[idb-persistence] collections write failed:", e);
    }
}
/**
 * Drop the persisted collection snapshot. Called from the sign-out
 * path alongside the block-envelope + stream-block-id clears so the
 * next login (potentially a different user) never briefly sees the
 * previous account's Groups / Collections / Stacks in any UI that
 * reads the cross-layer cache (nav switcher, "add to collection"
 * popover, homepage synthetic-stack lookup).
 */
export async function clearCollectionsSnapshot() {
    try {
        await del(COLLECTIONS_STORAGE_KEY);
    }
    catch (e) {
        console.warn("[idb-persistence] collections clear failed:", e);
    }
}
/**
 * Debounce a void-returning function. Trailing-edge — coalesces
 * bursts of calls (e.g. a `writeBatch` of N envelopes) into a single
 * invocation after `waitMs` of quiet.
 */
export function debounce(fn, waitMs) {
    let timer;
    return (...args) => {
        if (timer !== undefined)
            clearTimeout(timer);
        timer = setTimeout(() => {
            timer = undefined;
            fn(...args);
        }, waitMs);
    };
}
